import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const ThreeBackground = () => {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const starsRef = useRef([]);
  const galaxiesRef = useRef([]);
  const shootingStarsRef = useRef([]);
  let lastShootingStarTime = 0;
  const [isWebGLAvailable, setIsWebGLAvailable] = useState(true);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize Three.js scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 50;
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    // Create star field
    const createStarField = () => {
      const starGeometry = new THREE.BufferGeometry();
      const starCount = 1000;
      const positions = new Float32Array(starCount * 3);
      const colors = new Float32Array(starCount * 3);
      const sizes = new Float32Array(starCount);

      for (let i = 0; i < starCount * 3; i += 3) {
        // Random positions in a sphere
        const radius = 100 + Math.random() * 200;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(Math.random() * 2 - 1);

        positions[i] = radius * Math.sin(phi) * Math.cos(theta);
        positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
        positions[i + 2] = radius * Math.cos(phi);

        // Random colors (white, blue, yellow)
        const colorType = Math.random();
        if (colorType < 0.7) {
          colors[i] = 1.0; colors[i + 1] = 1.0; colors[i + 2] = 1.0; // White
        } else if (colorType < 0.85) {
          colors[i] = 0.6; colors[i + 1] = 0.8; colors[i + 2] = 1.0; // Blue
        } else {
          colors[i] = 1.0; colors[i + 1] = 0.9; colors[i + 2] = 0.6; // Yellow
        }

        sizes[i / 3] = Math.random() * 2 + 0.5;
      }

      starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      starGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

      // Create star material with custom shader
      const starMaterial = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 }
        },
        vertexShader: `
          attribute vec3 color;
          attribute float size;
          varying vec3 vColor;
          void main() {
            vColor = color;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = size * (300.0 / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform float time;
          varying vec3 vColor;
          void main() {
            float distance = length(gl_PointCoord - vec2(0.5));
            if (distance > 0.5) discard;
            // Twinkle effect: modulate brightness with time and position
            float twinkle = 0.7 + 0.3 * sin(time * 2.0 + gl_PointCoord.x * 12.0 + gl_PointCoord.y * 7.0);
            gl_FragColor = vec4(vColor * twinkle, 1.0);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });

      const starField = new THREE.Points(starGeometry, starMaterial);
      starsRef.current.push(starField);
      scene.add(starField);
    };

    // Create spiral galaxy with oval shape
    const createGalaxy = (position, color) => {
      const galaxyGeometry = new THREE.BufferGeometry();
      const starCount = 800;
      const positions = new Float32Array(starCount * 3);
      const colors = new Float32Array(starCount * 3);
      const sizes = new Float32Array(starCount);
      const angles = new Float32Array(starCount); // For rotation animation
      const speeds = new Float32Array(starCount); // For per-particle spin speed
      for (let i = 0; i < starCount; i++) {
        // Spiral arm index
        const arm = Math.floor(Math.random() * 3); // 3 arms
        const angle = Math.random() * Math.PI * 2;
        // Spiral: theta = angle + arm offset, radius = r
        const radius = Math.pow(Math.random(), 1.7) * 25; // denser core
        const armOffset = (arm / 3.0) * Math.PI * 2;
        // Spiral winding: add a twist based on radius
        const spiralAngle = angle + armOffset + radius * 0.25;
        // Scatter for realism
        const scatter = (Math.random() - 0.5) * 1.2;
        // Oval shape
        const ovalX = Math.cos(spiralAngle) * radius * 1.5 + scatter;
        const ovalY = Math.sin(spiralAngle) * radius * 0.8 + scatter;
        const ovalZ = (Math.random() - 0.5) * 2.5;
        positions[i * 3] = ovalX;
        positions[i * 3 + 1] = ovalY;
        positions[i * 3 + 2] = ovalZ;
        // Color gradient: core is white/yellow, arms are blue/purple/pink
        const t = radius / 25.0;
        let r, g, b;
        if (t < 0.2) { // Core
          r = 1.0; g = 0.95; b = 0.85;
        } else if (t < 0.5) { // Transition
          r = 1.0 - 0.5 * (t - 0.2) / 0.3;
          g = 0.95 - 0.3 * (t - 0.2) / 0.3;
          b = 0.85 + 0.3 * (t - 0.2) / 0.3;
        } else { // Arms
          const armColor = Math.random();
          if (armColor < 0.33) { r = 0.7; g = 0.8; b = 1.0; } // Blue
          else if (armColor < 0.66) { r = 0.9; g = 0.7; b = 1.0; } // Purple
          else { r = 1.0; g = 0.7; b = 0.9; } // Pink
        }
        colors[i * 3] = r;
        colors[i * 3 + 1] = g;
        colors[i * 3 + 2] = b;
        // Particle size: larger in core, smaller in arms
        sizes[i] = 2.5 - t * 1.5 + Math.random() * 0.7;
        angles[i] = spiralAngle; // Store spiral angle for rotation
        speeds[i] = 0.1 + Math.random() * 0.25; // Each particle gets a unique speed
      }

      galaxyGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      galaxyGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      galaxyGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      galaxyGeometry.setAttribute('angle', new THREE.BufferAttribute(angles, 1));
      galaxyGeometry.setAttribute('speed', new THREE.BufferAttribute(speeds, 1));

      const galaxyMaterial = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          createdAt: { value: 0 }
        },
        vertexShader: `
          attribute vec3 color;
          attribute float size;
          attribute float angle;
          attribute float speed;
          varying vec3 vColor;
          varying float vOpacity;
          uniform float time;
          uniform float createdAt;
          void main() {
            vColor = color;
            float age = time - createdAt;
            float fadeInDuration = 2.0;
            float fadeOutDuration = 2.0;
            float totalLifetime = 14.0;
            float opacity = 1.0;
            if (age < fadeInDuration) {
              opacity = age / fadeInDuration;
            } else if (age > totalLifetime - fadeOutDuration) {
              opacity = (totalLifetime - age) / fadeOutDuration;
            }
            vOpacity = opacity;
            float radius = length(position.xy);
            // Each particle spins along its original oval path, with unique speed
            float rotatedAngle = angle + age * speed;
            float ovalX = cos(rotatedAngle) * radius * 1.5;
            float ovalY = sin(rotatedAngle) * radius * 0.8;
            vec3 rotatedPosition = vec3(ovalX, ovalY, position.z);
            vec4 mvPosition = modelViewMatrix * vec4(rotatedPosition, 1.0);
            gl_PointSize = size * (120.0 / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform float time;
          varying vec3 vColor;
          varying float vOpacity;
          void main() {
            float distance = length(gl_PointCoord - vec2(0.5));
            if (distance > 0.5) discard;
            // Stronger glow for galaxy particles
            float twinkle = sin(time * 1.5 + gl_PointCoord.x * 8.0) * 0.2 + 1.0;
            gl_FragColor = vec4(vColor * twinkle, vOpacity);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });

      const galaxy = new THREE.Points(galaxyGeometry, galaxyMaterial);
      galaxy.position.copy(position);
      galaxy.userData = { createdAt: time };
      galaxy.material.uniforms.createdAt.value = time; // Set creation time to current animation time
      galaxiesRef.current.push(galaxy);
      scene.add(galaxy);
    };

    // Initialize scene
    createStarField();

    // Generate galaxies periodically
    const galaxyColors = [
      new THREE.Color(0.8, 0.9, 1.0), // Blue-white
      new THREE.Color(1.0, 0.8, 0.6), // Orange
      new THREE.Color(0.9, 0.7, 1.0), // Purple
      new THREE.Color(1.0, 0.9, 0.7), // Yellow
      new THREE.Color(0.7, 1.0, 0.8)  // Green
    ];

    const galaxyInterval = setInterval(() => {
      // Only generate if we have less than 5 galaxies
      if (galaxiesRef.current.length < 5) {
        const position = new THREE.Vector3(
          (Math.random() - 0.5) * 360, // x: [-180, 180]
          (Math.random() - 0.5) * 240, // y: [-120, 120]
          (Math.random() * -180) - 120 // z: [-300, -120], always far from camera
        );
        const color = galaxyColors[Math.floor(Math.random() * galaxyColors.length)];
        createGalaxy(position, color);
      }
    }, 8000); // Increased from 3000ms to 8000ms (8 seconds)

    // Scroll-based camera movement
    let scrollY = 0;
    const handleScroll = () => {
      scrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);

    // Animation loop
    let time = 0;
    const animate = () => {
      requestAnimationFrame(animate);
      time += 0.01;

      // Update star field
      starsRef.current.forEach(starField => {
        starField.material.uniforms.time.value = time;
        starField.rotation.y += 0.0000025; // Much slower rotation
      });

      // Update galaxies and remove old ones
      for (let i = galaxiesRef.current.length - 1; i >= 0; i--) {
        const galaxy = galaxiesRef.current[i];
        galaxy.material.uniforms.time.value = time;
        // Remove old galaxies (14 seconds, matching shader lifetime)
        if (time - galaxy.userData.createdAt > 14.0) {
          scene.remove(galaxy);
          galaxy.geometry.dispose();
          galaxy.material.dispose();
          galaxiesRef.current.splice(i, 1);
        }
      }

      // Shooting stars logic
      // Randomly spawn a shooting star every 2-6 seconds
      if (time - lastShootingStarTime > 2 + Math.random() * 4) {
        lastShootingStarTime = time;
        // Shooting star properties
        const startX = (Math.random() - 0.5) * 300;
        const startY = 120 + Math.random() * 80; // Start above the visible area
        const endX = startX + (Math.random() > 0.5 ? 1 : -1) * (100 + Math.random() * 100);
        const endY = startY - (60 + Math.random() * 60);
        const duration = 0.7 + Math.random() * 0.5;
        shootingStarsRef.current.push({
          startX, startY, endX, endY, startTime: time, duration, mesh: null, done: false
        });
      }
      // Animate and render shooting stars
      for (let i = shootingStarsRef.current.length - 1; i >= 0; i--) {
        const star = shootingStarsRef.current[i];
        const t = (time - star.startTime) / star.duration;
        if (t > 1) {
          if (star.mesh) scene.remove(star.mesh);
          shootingStarsRef.current.splice(i, 1);
          continue;
        }
        // Calculate position
        const x = star.startX + (star.endX - star.startX) * t;
        const y = star.startY + (star.endY - star.startY) * t;
        const z = -80 + Math.random() * 40; // In front of galaxies, behind stars
        // Create mesh if needed
        if (!star.mesh) {
          const geometry = new THREE.BufferGeometry();
          const positions = new Float32Array(6); // Line: 2 points
          geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
          const material = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true });
          const mesh = new THREE.Line(geometry, material);
          star.mesh = mesh;
          scene.add(mesh);
        }
        // Update geometry
        const positions = star.mesh.geometry.attributes.position.array;
        positions[0] = x;
        positions[1] = y;
        positions[2] = z;
        positions[3] = x - 10;
        positions[4] = y - 3;
        positions[5] = z;
        star.mesh.geometry.attributes.position.needsUpdate = true;
        // Fade out
        star.mesh.material.opacity = 1 - t;
      }

      // Scroll-based camera movement
      const scrollProgress = scrollY / (document.body.scrollHeight - window.innerHeight);
      camera.position.x = Math.sin(time * 0.1) * 30;
      camera.position.y = scrollProgress * 20 - 10; // Move camera based on scroll
      camera.position.z = 50 + Math.cos(time * 0.1) * 10;
      camera.lookAt(0, scrollProgress * 10 - 5, 0); // Look at point also moves with scroll

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearInterval(galaxyInterval);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      
      // Dispose of all resources
      const currentStars = starsRef.current;
      const currentGalaxies = galaxiesRef.current;
      
      currentStars.forEach(starField => {
        starField.geometry.dispose();
        starField.material.dispose();
      });
      
      currentGalaxies.forEach(galaxy => {
        galaxy.geometry.dispose();
        galaxy.material.dispose();
      });

      // Remove shooting stars
      shootingStarsRef.current.forEach(star => {
        if (star.mesh) scene.remove(star.mesh);
      });
      
      renderer.dispose();
    };
  }, []);

  return (
    <>
      <canvas 
        ref={canvasRef} 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1
        }}
      />
      {!isWebGLAvailable && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          textAlign: 'center',
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '20px',
          borderRadius: '8px',
          zIndex: 1000
        }}>
          <h3>WebGL is not available</h3>
          <p>Please try a different browser or enable hardware acceleration.</p>
        </div>
      )}
    </>
  );
};

export default ThreeBackground;